import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  Package,
  FileText,
  FileSignature,
  Upload,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";
import InvoicesSection from "./InvoicesSection";
import QuotesSection from "./QuotesSection";

const emptyClient = {
  name: "",
  nif: "",
  group: "",
  address: "",
  postal_code: "",
  city: "",
  province: "",
  country: "España",
  phone: "",
  email: "",
  contact_name: "",
  website: "",
  payment_method: "TRANSFERENCIA BANCARIA",
  iban: "",
  default_vat_rate: 0,
  default_irpf_rate: 0,
  is_public_admin: false,
  dir3_oficina_contable: "",
  dir3_oficina_contable_name: "",
  dir3_organo_gestor: "",
  dir3_organo_gestor_name: "",
  dir3_unidad_tramitadora: "",
  dir3_unidad_tramitadora_name: "",
  dir3_organo_proponente: "",
  dir3_organo_proponente_name: "",
  notes: "",
};

const emptyProduct = {
  code: "",
  name: "",
  description: "",
  unit_price: 0,
  vat_rate: 0,
  irpf_rate: 0,
};

const sortAlpha = (arr, key = "name") =>
  [...arr].sort((a, b) =>
    String(a?.[key] || "").localeCompare(
      String(b?.[key] || ""),
      "es",
      { sensitivity: "base" }
    )
  );

// Reusable Excel importer button. On file select, POSTs multipart/form-data
// to `endpoint` and toasts the result. Calls `onSuccess()` when done.
function ImportExcelButton({ endpoint, onSuccess, testid, disabled }) {
  const inputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported = 0, skipped = 0, errors = [] } = res.data || {};
      if (errors.length) {
        toast.warning(
          `Importados ${imported}, saltados ${skipped}, ${errors.length} errores.`,
          { description: errors.slice(0, 3).join(" · ") }
        );
      } else {
        toast.success(
          `Importados ${imported}${skipped ? `, saltados ${skipped}` : ""}.`
        );
      }
      onSuccess?.();
    } catch (err) {
      logError("Error importando Excel", err);
      toast.error(
        err?.response?.data?.detail ||
          "No se pudo importar el archivo. Revisa el formato."
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testid}-file-input`}
      />
      <Button
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || importing}
        className="h-10 px-4"
        data-testid={testid}
      >
        <Upload className="h-4 w-4 mr-2" />
        {importing ? "Importando…" : "Importar Excel"}
      </Button>
    </>
  );
}

// ============================================================================
// CLIENTES
// ============================================================================
function ClientsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      const hay = [
        c.name,
        c.nif,
        c.city,
        c.province,
        c.email,
        c.phone,
        c.contact_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/invoicing/clients");
      setItems(sortAlpha(res.data || []));
    } catch (e) {
      logError("No se pudieron cargar los clientes de facturación", e);
      toast.error("No se pudieron cargar los clientes.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyClient);
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ ...emptyClient, ...it });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre del cliente es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_vat_rate: parseFloat(form.default_vat_rate) || 0,
        default_irpf_rate: parseFloat(form.default_irpf_rate) || 0,
      };
      if (editingId) {
        await adminApi.put(`/admin/invoicing/clients/${editingId}`, payload);
        toast.success("Cliente actualizado.");
      } else {
        await adminApi.post("/admin/invoicing/clients", payload);
        toast.success("Cliente creado.");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      logError("Error al guardar el cliente", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar el cliente.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admin/invoicing/clients/${deleteTarget.id}`);
      toast.success("Cliente eliminado.");
      setDeleteTarget(null);
      fetchItems();
    } catch (e) {
      logError("Error al eliminar el cliente", e);
      toast.error("No se pudo eliminar el cliente.");
    }
  };

  return (
    <div data-testid="invoicing-clients-section">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Clientes
          </h3>
          <p className="text-sm text-zinc-500">
            Base de datos de clientes para las facturas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ImportExcelButton
            endpoint="/admin/invoicing/clients/import"
            onSuccess={fetchItems}
            testid="invoicing-clients-import-btn"
          />
          <Button
            onClick={openCreate}
            className="bg-zinc-950 hover:bg-zinc-800 text-white h-10 px-4"
            data-testid="invoicing-client-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo cliente
          </Button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, NIF, ciudad, email…"
          className="pl-10 border-zinc-300"
          data-testid="invoicing-clients-search"
        />
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-8">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 border-2 border-dashed border-zinc-300 rounded-md">
          Todavía no hay clientes. Añade el primero con "Nuevo cliente".
        </div>
      ) : filteredItems.length === 0 ? (
        <div
          className="text-center text-zinc-500 py-8"
          data-testid="invoicing-clients-empty-search"
        >
          Ningún cliente coincide con "<strong>{search}</strong>".
        </div>
      ) : (
        <div className="space-y-2" data-testid="invoicing-clients-list">
          <div className="text-xs text-zinc-500 mb-1">
            Mostrando {filteredItems.length} de {items.length} clientes.
          </div>
          {filteredItems.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between gap-4 p-4 bg-white border border-zinc-200 rounded-md hover:border-zinc-400 transition-colors"
              data-testid={`invoicing-client-row-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-zinc-900 truncate flex items-center gap-2">
                  <span className="truncate">{c.name}</span>
                  {c.group === "ISSEFE" && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800"
                      data-testid={`invoicing-client-badge-issefe-${c.id}`}
                    >
                      ISSEFE
                    </span>
                  )}
                  {c.group === "DESFIEMER" && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-800"
                      data-testid={`invoicing-client-badge-desfiemer-${c.id}`}
                    >
                      DESFIEMER
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1 flex gap-3 flex-wrap">
                  {c.nif && <span>NIF: {c.nif}</span>}
                  {c.city && <span>{c.city}</span>}
                  {c.phone && <span>Tel: {c.phone}</span>}
                  {c.email && <span>{c.email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(c)}
                  data-testid={`invoicing-client-edit-${c.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(c)}
                  className="text-rose-600 hover:bg-rose-50"
                  data-testid={`invoicing-client-delete-${c.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="invoicing-client-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar cliente" : "Nuevo cliente"}
            </DialogTitle>
            <DialogDescription>
              Los datos fiscales aparecerán en las facturas emitidas a este
              cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Razón social *
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="invoicing-client-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Grupo
              </label>
              <Select
                value={form.group || "__none__"}
                onValueChange={(v) =>
                  setForm({ ...form, group: v === "__none__" ? "" : v })
                }
              >
                <SelectTrigger data-testid="invoicing-client-group">
                  <SelectValue placeholder="Cualquier grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin asignar —</SelectItem>
                  <SelectItem value="DESFIEMER">DESFIEMER</SelectItem>
                  <SelectItem value="ISSEFE">ISSEFE</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-zinc-500">
                Al emitir factura, el desplegable de cursos mostrará solo
                los cursos visibles para este grupo.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                NIF / CIF
              </label>
              <Input
                value={form.nif}
                onChange={(e) => setForm({ ...form, nif: e.target.value })}
                data-testid="invoicing-client-nif"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Persona de contacto
              </label>
              <Input
                value={form.contact_name}
                onChange={(e) =>
                  setForm({ ...form, contact_name: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Dirección
              </label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Código postal
              </label>
              <Input
                value={form.postal_code}
                onChange={(e) =>
                  setForm({ ...form, postal_code: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Ciudad
              </label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Provincia
              </label>
              <Input
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                País
              </label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Teléfono
              </label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Email
              </label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Web
              </label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Forma de pago
              </label>
              <Input
                value={form.payment_method}
                onChange={(e) =>
                  setForm({ ...form, payment_method: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                IBAN
              </label>
              <Input
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                IVA por defecto (%)
              </label>
              <Input
                type="number"
                step="0.01"
                value={form.default_vat_rate}
                onChange={(e) =>
                  setForm({ ...form, default_vat_rate: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                IRPF por defecto (%)
              </label>
              <Input
                type="number"
                step="0.01"
                value={form.default_irpf_rate}
                onChange={(e) =>
                  setForm({ ...form, default_irpf_rate: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2 border-t border-zinc-200 pt-4">
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.is_public_admin}
                  onChange={(e) =>
                    setForm({ ...form, is_public_admin: e.target.checked })
                  }
                  className="h-4 w-4"
                  data-testid="invoicing-client-public-admin"
                />
                <span className="text-sm font-semibold text-zinc-900">
                  Es una administración pública (e-factura por FACe)
                </span>
              </label>
              {form.is_public_admin && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    ["dir3_oficina_contable", "Oficina contable (DIR3)"],
                    ["dir3_organo_gestor", "Órgano gestor (DIR3)"],
                    ["dir3_unidad_tramitadora", "Unidad tramitadora (DIR3)"],
                    ["dir3_organo_proponente", "Órgano proponente (opcional)"],
                  ].map(([field, label]) => (
                    <div key={field}>
                      <label className="text-sm font-medium text-zinc-700 mb-1 block">
                        {label}
                      </label>
                      <div className="flex gap-2">
                        <Input
                          value={form[field] || ""}
                          placeholder="L01470770"
                          onChange={(e) =>
                            setForm({ ...form, [field]: e.target.value.toUpperCase() })
                          }
                          className="font-mono w-40"
                          data-testid={`invoicing-client-${field}`}
                        />
                        <Input
                          value={form[`${field}_name`] || ""}
                          placeholder="Nombre del centro"
                          onChange={(e) =>
                            setForm({ ...form, [`${field}_name`]: e.target.value })
                          }
                          data-testid={`invoicing-client-${field}_name`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Notas internas
              </label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-client-save-btn"
            >
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cliente</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres eliminar el cliente{" "}
              <strong>{deleteTarget?.name}</strong>? Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="invoicing-client-confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// PRODUCTOS
// ============================================================================
function ProductsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const hay = [p.name, p.code, p.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/invoicing/products");
      setItems(sortAlpha(res.data || []));
    } catch (e) {
      logError("No se pudieron cargar los productos de facturación", e);
      toast.error("No se pudieron cargar los productos.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyProduct);
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ ...emptyProduct, ...it });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre del producto es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_price: parseFloat(form.unit_price) || 0,
        vat_rate: parseFloat(form.vat_rate) || 0,
        irpf_rate: parseFloat(form.irpf_rate) || 0,
      };
      if (editingId) {
        await adminApi.put(`/admin/invoicing/products/${editingId}`, payload);
        toast.success("Producto actualizado.");
      } else {
        await adminApi.post("/admin/invoicing/products", payload);
        toast.success("Producto creado.");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      logError("Error al guardar el producto", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar el producto.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admin/invoicing/products/${deleteTarget.id}`);
      toast.success("Producto eliminado.");
      setDeleteTarget(null);
      fetchItems();
    } catch (e) {
      logError("Error al eliminar el producto", e);
      toast.error("No se pudo eliminar el producto.");
    }
  };

  return (
    <div data-testid="invoicing-products-section">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Productos / Servicios
          </h3>
          <p className="text-sm text-zinc-500">
            Catálogo que aparecerá al añadir líneas a una factura.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ImportExcelButton
            endpoint="/admin/invoicing/products/import"
            onSuccess={fetchItems}
            testid="invoicing-products-import-btn"
          />
          <Button
            onClick={openCreate}
            className="bg-zinc-950 hover:bg-zinc-800 text-white h-10 px-4"
            data-testid="invoicing-product-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo producto
          </Button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, código o descripción…"
          className="pl-10 border-zinc-300"
          data-testid="invoicing-products-search"
        />
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-8">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 border-2 border-dashed border-zinc-300 rounded-md">
          Todavía no hay productos. Añade el primero con "Nuevo producto".
        </div>
      ) : filteredItems.length === 0 ? (
        <div
          className="text-center text-zinc-500 py-8"
          data-testid="invoicing-products-empty-search"
        >
          Ningún producto coincide con "<strong>{search}</strong>".
        </div>
      ) : (
        <div className="space-y-2" data-testid="invoicing-products-list">
          <div className="text-xs text-zinc-500 mb-1">
            Mostrando {filteredItems.length} de {items.length} productos.
          </div>
          {filteredItems.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between gap-4 p-4 bg-white border border-zinc-200 rounded-md hover:border-zinc-400 transition-colors"
              data-testid={`invoicing-product-row-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-zinc-900 truncate">
                  {p.name}
                </div>
                <div className="text-xs text-zinc-500 mt-1 flex gap-3 flex-wrap">
                  {p.code && <span>Código: {p.code}</span>}
                  <span>Base: {Number(p.unit_price).toFixed(2)} €</span>
                  <span>IVA: {p.vat_rate}%</span>
                  {p.irpf_rate > 0 && <span>IRPF: {p.irpf_rate}%</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(p)}
                  data-testid={`invoicing-product-edit-${p.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(p)}
                  className="text-rose-600 hover:bg-rose-50"
                  data-testid={`invoicing-product-delete-${p.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="invoicing-product-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar producto" : "Nuevo producto"}
            </DialogTitle>
            <DialogDescription>
              Se usará como plantilla al añadir líneas a las facturas.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Nombre *
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="invoicing-product-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Código
              </label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Precio base (€)
              </label>
              <Input
                type="number"
                step="0.01"
                value={form.unit_price}
                onChange={(e) =>
                  setForm({ ...form, unit_price: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                IVA (%)
              </label>
              <Input
                type="number"
                step="0.01"
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                IRPF (%)
              </label>
              <Input
                type="number"
                step="0.01"
                value={form.irpf_rate}
                onChange={(e) =>
                  setForm({ ...form, irpf_rate: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Descripción
              </label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-product-save-btn"
            >
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar producto</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres eliminar <strong>{deleteTarget?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="invoicing-product-confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// FACTURAS — el componente vive en `InvoicesSection.jsx` para mantener el
// tamaño de este archivo manejable.
// ============================================================================

// ============================================================================
// Main manager con sub-tabs
// ============================================================================
export default function InvoicingManager() {
  return (
    <div data-testid="invoicing-manager">
      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-md p-1 h-auto flex-wrap mb-4">
          <TabsTrigger
            value="invoices"
            className="data-[state=active]:bg-white data-[state=active]:text-zinc-950 text-zinc-600 rounded-sm h-9 px-4 text-sm font-semibold transition-colors"
            data-testid="invoicing-tab-invoices"
          >
            <FileText className="h-4 w-4 mr-2" />
            Facturas
          </TabsTrigger>
          <TabsTrigger
            value="quotes"
            className="data-[state=active]:bg-white data-[state=active]:text-zinc-950 text-zinc-600 rounded-sm h-9 px-4 text-sm font-semibold transition-colors"
            data-testid="invoicing-tab-quotes"
          >
            <FileSignature className="h-4 w-4 mr-2" />
            Presupuestos
          </TabsTrigger>
          <TabsTrigger
            value="clients"
            className="data-[state=active]:bg-white data-[state=active]:text-zinc-950 text-zinc-600 rounded-sm h-9 px-4 text-sm font-semibold transition-colors"
            data-testid="invoicing-tab-clients"
          >
            <Building2 className="h-4 w-4 mr-2" />
            Clientes
          </TabsTrigger>
          <TabsTrigger
            value="products"
            className="data-[state=active]:bg-white data-[state=active]:text-zinc-950 text-zinc-600 rounded-sm h-9 px-4 text-sm font-semibold transition-colors"
            data-testid="invoicing-tab-products"
          >
            <Package className="h-4 w-4 mr-2" />
            Productos / Servicios
          </TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          <InvoicesSection />
        </TabsContent>
        <TabsContent value="quotes">
          <QuotesSection />
        </TabsContent>
        <TabsContent value="clients">
          <ClientsSection />
        </TabsContent>
        <TabsContent value="products">
          <ProductsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
